import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { FlexiAdvisorSettingsService } from './flexi-advisor-settings.service';
import {
  FlexiAdvisorAssignment,
  FlexiAdvisorBulkMode,
  FlexiAdvisorWorkload,
} from './flexi-advisor-settings.models';
import { initials, ReplaceRemoval, replaceRemovals } from './flexi-advisor-settings.logic';

/** How many business names the dialog lists before summarising the rest. */
const NAMED_BUSINESSES = 5;

/**
 * Assigns the same advisors to every selected business in one request.
 *
 * The order the admin ticks advisors in is the priority order. Append keeps
 * each business's current list, and so its primary, and adds only who is
 * missing; Replace makes every business's list exactly the ticked one, and the
 * dialog names who that removes before anything is sent.
 */
@Component({
  selector: 'flexi-advisor-bulk-dialog',
  templateUrl: './advisor-bulk-dialog.component.html',
  styleUrls: ['./advisor-bulk-dialog.component.scss'],
  standalone: false,
})
export class FlexiAdvisorBulkDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) businesses: FlexiAdvisorAssignment[] = [];
  /** Active admins of the application: the only advisors that can be assigned. */
  @Input() selectable: FlexiAdvisorWorkload[] = [];
  @Output() closed = new EventEmitter<boolean>();

  readonly initials = initials;

  visible = true;
  /** Ticked advisors, in tick order. */
  ticked: number[] = [];
  mode: FlexiAdvisorBulkMode = 'Append';
  removals: ReplaceRemoval[] = [];
  namedBusinesses = '';
  otherBusinesses = 0;
  saving = false;
  saveFailed = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiAdvisorSettingsService,
    private notifications: NotificationService,
  ) {}

  ngOnInit(): void {
    const names = this.businesses.map(business => business.CompanyName ?? '');
    this.namedBusinesses = names.slice(0, NAMED_BUSINESSES).join(', ');
    this.otherBusinesses = Math.max(0, names.length - NAMED_BUSINESSES);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** 1-based priority of a ticked advisor, or 0 when not ticked. */
  order(userId: number): number {
    return this.ticked.indexOf(userId) + 1;
  }

  toggle(userId: number): void {
    this.ticked = this.order(userId) > 0
      ? this.ticked.filter(id => id !== userId)
      : [...this.ticked, userId];
    this.refreshRemovals();
  }

  modeChanged(mode: FlexiAdvisorBulkMode): void {
    this.mode = mode;
    this.refreshRemovals();
  }

  submit(): void {
    if (this.ticked.length === 0 || this.saving) {
      return;
    }
    this.saving = true;
    this.saveFailed = false;
    this.api
      .bulkAssign({
        BusinessIds: this.businesses.map(business => business.BusinessId),
        UserIds: this.ticked,
        Mode: this.mode,
      })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifications.success('FLEXI_SETTINGS.BULK_DONE');
          this.close(true);
        },
        error: () => (this.saveFailed = true),
      });
  }

  cancel(): void {
    this.close(false);
  }

  visibleChanged(visible: boolean): void {
    if (!visible) {
      this.close(false);
    }
  }

  private close(saved: boolean): void {
    this.visible = false;
    this.closed.emit(saved);
  }

  private refreshRemovals(): void {
    this.removals = this.mode === 'Replace' && this.ticked.length > 0
      ? replaceRemovals(this.businesses, this.ticked)
      : [];
  }
}
